import React from "react";
import PropTypes from "prop-types";
import { injectIntl, intlShape } from "@edx/frontend-platform/i18n";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router";
import { getConfig } from "@edx/frontend-platform";
import { toast } from "react-toastify";
import Cookies from "universal-cookie";

import Footer from "@edx/frontend-component-footer";
import { Toast } from "@openedx/paragon";
import PageLoading from "../generic/PageLoading";
import { getAccessDeniedRedirectUrl } from "../shared/access";
import { useModel } from "../generic/model-store";
import genericMessages from "../generic/messages";
import messages from "./messages";
import LoadedTabPage from "./LoadedTabPage";
import { setCallToActionToast } from "../course-home/data/slice";
import LaunchCourseHomeTourButton from "../product-tours/newUserCourseHomeTour/LaunchCourseHomeTourButton";

import { ReactComponent as Info } from "../svg/info.svg";
import { parseJwt } from "../utils/utility";
import CustomFooter from "../footer/CustomFooter";

import { getAuthenticatedHttpClient } from "@edx/frontend-platform/auth";
import CustomHeader from "../header/CustomHeader";
import FullPageSkeleton from "../components/Skeletons/FullPageSkeleton";

const handleReleaseHijackUser = async (setIsReleasingHijackUser) => {
  setIsReleasingHijackUser(true);

  try {
    const { data } = await getAuthenticatedHttpClient().get(
      `${getConfig().LMS_BASE_URL}${getConfig().CSRF_TOKEN_API_PATH}`,
      {}
    );

    await getAuthenticatedHttpClient().post(
      `${getConfig().LMS_BASE_URL}/hijack/release/`,
      {},
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-CSRFToken": data?.csrfToken,
        },
      }
    );

    toast.success(
      `You have successfully logged out from the compromised user account.`
    );
    window.location.replace(
      `${getConfig().PANEL_BASE_URL}/support/impersonate`
    );
  } catch (error) {
    toast.error(
      `Oops! Something went wrong. Please try refreshing the page to see if that resolves the issue.`
    );
  } finally {
    setIsReleasingHijackUser(false);
  }
};
function TabPage({
  intl,
  activeTabSlug,
  courseId = null,
  courseStatus,
  metadataModel,
  unitId = null,
  ...restProps
}) {
  const { toastBodyLink, toastBodyText, toastHeader } = useSelector(
    (state) => state.courseHome
  );
  const dispatch = useDispatch();
  const { canLoadCourseware, courseAccess, number, org, start, title } =
    useModel("courseHomeMeta", courseId);

  const [isReleasingHijackUser, setIsReleasingHijackUser] =
    React.useState(false);

  const cookies = new Cookies();
  const jwtToken = cookies.get("edx-jwt-cookie-header-payload");
  const parsedJwtToken = parseJwt(jwtToken);

  if (courseStatus === "loading") {
    return (
      <>
        <CustomHeader
          org={org}
          number={number}
          title={title}
          name={parsedJwtToken.first_name}
        />
        <FullPageSkeleton srMessage={intl.formatMessage(messages.loading)} />
        <Footer />
      </>
    );
  }

  if (courseStatus === "denied") {
    const redirectUrl = getAccessDeniedRedirectUrl(
      courseId,
      activeTabSlug,
      canLoadCourseware,
      courseAccess,
      start,
      unitId
    );
    if (redirectUrl) {
      return <Navigate to={redirectUrl} replace />;
    }
  }

  // Either a success state or a denied state that wasn't redirected above (some tabs handle denied states themselves,
  // like the outline tab handling unenrolled learners)
  if (courseStatus === "loaded" || courseStatus === "denied") {
    return (
      <>
        <Toast
          action={
            toastBodyText
              ? {
                  label: toastBodyText,
                  href: toastBodyLink,
                }
              : null
          }
          closeLabel={intl.formatMessage(genericMessages.close)}
          onClose={() =>
            dispatch(
              setCallToActionToast({ header: "", link: null, link_text: null })
            )
          }
          show={!!toastHeader}
        >
          {toastHeader}
        </Toast>
        {metadataModel === "courseHomeMeta" && (
          <LaunchCourseHomeTourButton srOnly />
        )}
        <CustomHeader
          org={org}
          number={number}
          title={title}
          name={parsedJwtToken.first_name}
        />
        {parsedJwtToken?.is_hijacked && (
          <div className="hijacked-alert-holder">
            <div className="hijacked-alert">
              <div className="alert-info">
                <div className="alert-icon">
                  <Info />
                </div>
                <div className="alert-message">
                  <strong>
                    You are now Impersonating another user. Please note that you
                    will not be able to make any updates on behalf of this user.
                  </strong>
                </div>
                <div className="alert-action-btn">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      handleReleaseHijackUser(setIsReleasingHijackUser);
                    }}
                  >
                    Exit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {isReleasingHijackUser && (
          <FullPageSkeleton 
            className="overlay-layout" 
            isHeader={true} 
            srMessage={intl.formatMessage(messages.loading)} 
          />
        )}
        <LoadedTabPage
          {...restProps}
          activeTabSlug={activeTabSlug}
          courseId={courseId}
          courseStatus={courseStatus}
          metadataModel={metadataModel}
          unitId={unitId}
        />
        <CustomFooter />
        <Footer />
      </>
    );
  }

  // courseStatus 'failed' and any other unexpected course status.
  return (
    <>
      <CustomHeader
        org={org}
        number={number}
        title={title}
        name={parsedJwtToken.first_name}
      />
      <p className="text-center py-5 mx-auto" style={{ maxWidth: "30em" }}>
        {intl.formatMessage(messages.failure)}
      </p>
      <Footer />
    </>
  );
}

TabPage.propTypes = {
  activeTabSlug: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  courseId: PropTypes.string,
  courseStatus: PropTypes.string.isRequired,
  metadataModel: PropTypes.string.isRequired,
  unitId: PropTypes.string,
};

export default injectIntl(TabPage);
